import { useEffect, useState } from "react";

import { isLoggedIn, Spinner } from "@shapr3d/frontend-lib";
import { Icon_Comment24 } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { useParams } from "react-router-dom";

import { type CommentID, useComments } from "../../hooks/useComments";
import { useTracker } from "../../hooks/useTracker";
import { useWebExport } from "../../hooks/useWebExport";
import { useWindowSize, ViewportType } from "../../hooks/useWindowSize";
import { useCommentsStore } from "../../stores/commentStore";
import { Comment } from "../Comment/Comment";
import { Sidebar } from "../Sidebar/Sidebar";
import { ToolButton } from "../ToolButton/ToolButton";

import style from "./CommentSidebar.module.css";

interface CommentSidebarProps {
  isOpen: boolean;
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const CommentSidebar = ({ isOpen, setIsOpen }: CommentSidebarProps) => {
  const tracker = useTracker();
  const { viewLinkId = "" } = useParams();
  const {
    data: commentsData,
    isSuccess: isCommentsSuccess,
    isError: isCommentsFetchError,
  } = useComments();
  const { data: webExportData, isSuccess: isWebExportSuccess } = useWebExport();
  const { setOpenCommentId, openCommentId } = useCommentsStore();
  const { viewportType } = useWindowSize();

  const handleCommentClick = (commentId: CommentID) => {
    tracker.trackCommentSelected({
      commentId,
      webExportId: webExportData?.webExport.webExportId,
      source: "sidebar",
      viewLinkId,
    });

    setOpenCommentId(commentId);
  };

  const isLoaded = isCommentsSuccess && isWebExportSuccess;

  // Open the sidebar by default
  const [isFirstFetch, setIsFirstFetch] = useState(true);
  useEffect(() => {
    if (isCommentsSuccess && isFirstFetch && viewportType !== ViewportType.Mobile) {
      tracker.trackCommentingDisplayed({
        viewLinkId,
        webExportId: webExportData?.webExport.webExportId,
      });
      setIsFirstFetch(false);
      setIsOpen?.(true);
    }
  }, [
    isCommentsSuccess,
    isFirstFetch,
    setIsOpen,
    setIsFirstFetch,
    tracker,
    viewLinkId,
    viewportType,
    webExportData,
  ]);

  return (
    <Sidebar
      title="Comments"
      position="right"
      isOpen={isOpen}
      onClose={() => setIsOpen?.(false)}
      controlButtons={
        <ToolButton
          title="Comments"
          alt="Comments"
          icon={<Icon_Comment24 />}
          onClick={() => {
            setIsOpen?.((isOpen) => {
              const newValue = !isOpen;
              tracker.trackCommentingSidebarVisibilityChanged({
                viewLinkId,
                visible: newValue,
              });
              return newValue;
            });
          }}
          isActive={isOpen}
        />
      }
    >
      {!isLoggedIn() && webExportData?.baseRole === "viewer" && (
        <div className={style.emptyContent}>
          <div className={style.text}>
            Gather feedback on your designs!
            <br></br>
            Log in to manage sharing settings and enable commenting.
          </div>
        </div>
      )}

      {isCommentsFetchError && (
        <div className={style.emptyContent}>
          <div className={style.text}>
            Something went wrong while loading comments. Please try again later.
          </div>
        </div>
      )}

      {!isLoaded && isLoggedIn() && (
        <div className={style.emptyContent}>
          <Spinner size={20} />
        </div>
      )}

      {isLoaded && !commentsData?.comments?.length && (
        <div className={style.emptyContent}>
          <div className={style.text}>
            No comments yet. To place a comment use the Add Comment tool or Ctrl+Shift+click on the
            model.
          </div>
        </div>
      )}

      {commentsData?.comments?.map((comment) => {
        return (
          <div
            key={comment.commentId}
            className={style.commentSidebarBox}
            onClick={() => handleCommentClick(comment.commentId)}
          >
            <div
              className={classNames(
                style.commentSidebarCard,
                comment.commentId === openCommentId && style.active
              )}
            >
              <Comment
                isTruncated
                initialMode="view"
                title={commentsData?.users[comment.createdByUserId] ?? "Unknown user"}
                date={comment.createdAt}
                actions={<Comment.ContextMenu comment={comment} />}
              >
                {comment.message}
              </Comment>
            </div>
          </div>
        );
      })}
    </Sidebar>
  );
};
